import React, { useEffect, useState } from 'react';
import axios from '../../utils/axios';
import Sidebar from './sidebar';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import resolveImageUrl from '../../utils/imageUtils';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  FaBuilding,
  FaCheckCircle,
  FaUserTie,
  FaExclamationCircle,
  FaChartPie,
  FaFlag,
  FaHourglassHalf,
  FaClipboardList,
} from 'react-icons/fa';
import { trackAction, setCurrentSupervisorId } from '../../utils/clickTracking';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProperties: 0,
    totalCircles: 0,
    inProcess: 0,
    resolved: 0,
    inspectors: 0,
    supervisors: 0,
    awaitingExplanations: 0,
  });
  const [assignedCount, setAssignedCount] = useState(0);
  const [assignedByCircle, setAssignedByCircle] = useState({});
  const [allCircles, setAllCircles] = useState([]);
  const [showCirclesList, setShowCirclesList] = useState(false);

  // ✅ Search state
  const [pinSearch, setPinSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchedProperty, setSearchedProperty] = useState(null);
  const [searchMessage, setSearchMessage] = useState('');

  const navigate = useNavigate();

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();
    switch (normalizedStatus) {
      case "resolved":
        return <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">Resolved</span>;
      case "flagged":
        return <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">Flagged</span>;
      case "assigned":
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">Assigned</span>;
      case "pending":
        return <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">Pending</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">{status || "N/A"}</span>;
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const currentUserId = user?.id ?? user?.userId;
    if (currentUserId) {
      setCurrentSupervisorId(currentUserId);
      trackAction('dashboard', null, null, {}, 'supervisor');
    }
    
    const fetchDashboardStats = async () => {
      try {
        const [userStatsRes, totalCirclesRes, circlesListRes] = await Promise.all([
          axios.get('/users/dashboard-stats'),
          axios.get('/properties/total-circles'),
          axios.get('/properties/circles'),
        ]);
        setStats(prevStats => ({
          ...prevStats,
          ...userStatsRes.data,
          totalCircles: totalCirclesRes.data.totalCircles,
        }));
        setAllCircles(circlesListRes.data || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load dashboard stats');
      }
    };
    fetchDashboardStats();

    const fetchAssignedCount = async () => {
      try {
        const res = await axios.get("/properties", { params: { limit: 10000 } });
        const list = res.data?.data || [];
        
        const assigned = list.filter((p) => 
          ["assigned", "flagged"].includes(p.status) && 
          p.supervisor_user_id === currentUserId
        );
        
        // Group by circle
        const grouped = {};
        assigned.forEach(p => {
          const circle = p.circle || "Uncategorized";
          if (!grouped[circle]) {
            grouped[circle] = 0;
          }
          grouped[circle]++;
        });
        
        setAssignedCount(assigned.length);
        setAssignedByCircle(grouped);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load assigned properties');
      }
    };
    fetchAssignedCount();
  }, []);

  // ✅ Search handler — searches all properties by PIN
  const handleSearchByPin = async () => {
    const pin = pinSearch.trim();
    if (!pin) {
      setSearchMessage('Please enter a PIN.');
      setSearchedProperty(null);
      return;
    }

    trackAction('property_search', null, null, { searched_pin: pin }, 'supervisor');
    
    try {
      setSearching(true);
      setSearchMessage('');
      setSearchedProperty(null);

      // Fetch all properties and search client-side (same pattern as inspector)
      const res = await axios.get('/properties', { params: { limit: 10000 } });
      const list = res.data?.data || [];
      const found = list.find(
        (p) => String(p.pin || '').trim().toLowerCase() === pin.toLowerCase()
      );

      if (!found) {
        setSearchMessage('No property found against this PIN.');
        return;
      }

      setSearchedProperty(found);
    } catch (err) {
      console.error(err);
      setSearchMessage('Failed to search property by PIN.');
    } finally {
      setSearching(false);
    }
  };

  const cardData = [
    {
      title: 'Total Circles',
      value: stats.totalCircles,
      icon: <FaChartPie />,
      route: null,
      action: 'total_circles_view',
    },
    {
      title: 'Inspectors',
      value: stats.inspectors,
      icon: <FaUserTie />,
      route: '/supervisor/Inspectors',
      action: 'inspectors_view',
    },
    {
      title: 'Total Properties',
      value: stats.totalProperties,
      icon: <FaBuilding />,
      route: '/supervisor/property',
      action: 'property_view',
    },
    {
      title: 'Resolved Properties',
      value: stats.resolved,
      icon: <FaCheckCircle />,
      route: '/supervisor/resolved',
      action: 'resolved_view',
    },
    {
      title: 'Assigned to Inspector',
      value: stats.awaitingExplanations,
      icon: <FaExclamationCircle />,
      route: '/supervisor/flaged',
      action: 'flagged_view',
    },
  ];

  const toSafeNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  const totalProperties = toSafeNumber(stats.totalProperties) || 1;
  const resolvedCount = toSafeNumber(stats.resolved);
  const awaitingCount = toSafeNumber(stats.awaitingExplanations);
  const remaining = Math.max(0, totalProperties - resolvedCount - awaitingCount);

  // Prepare data for pie chart
  const pieChartData = [
    { name: 'Resolved', value: resolvedCount, color: '#10B981' },
    { name: 'Flagged/Assigned', value: awaitingCount, color: '#EF4444' },
    { name: 'Pending', value: remaining, color: '#9CA3AF' },
  ].filter(item => item.value > 0);

  const resolvedPercentage = Math.round((resolvedCount / totalProperties) * 100);
  const flaggedPercentage = Math.round((awaitingCount / totalProperties) * 100);
  const remainingPercentage = Math.max(0, 100 - resolvedPercentage - flaggedPercentage);

  return (
    <div className="flex-col min-h-screen bg-gray-100 text-gray-800">
      <Sidebar />
      <main className="flex-1 p-8 overflow-y-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-semibold text-gray-800">Dashboard Overview</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's what's happening today.</p>
        </div>

        {/* ✅ PIN Search Bar - Moved to top */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Search Property by PIN</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={pinSearch}
              onChange={(e) => setPinSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchByPin();
                }
              }}
              placeholder="Enter exact PIN..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            />
            <button
              onClick={handleSearchByPin}
              disabled={searching}
              className="px-5 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-60"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchMessage && (
            <p className="mt-3 text-sm text-gray-600">{searchMessage}</p>
          )}

          {searchedProperty && (
            <div className="mt-4 border border-teal-200 rounded-lg p-4 bg-teal-50">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {searchedProperty.image ? (
                    <img
                      src={resolveImageUrl(searchedProperty.image)}
                      alt="Property"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-500">No Image</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700"><span className="font-semibold">PIN:</span> {searchedProperty.pin}</p>
                  <p className="text-sm text-gray-700"><span className="font-semibold">Circle:</span> {searchedProperty.circle || 'N/A'}</p>
                  <p className="text-sm text-gray-700"><span className="font-semibold">Location:</span> {searchedProperty.location || 'N/A'}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 font-semibold">Status:</span>
                    {getStatusBadge(searchedProperty.status)}
                  </div>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Land Area:</span>{" "}
                    {hasUsableValue(searchedProperty.landarea_admin) ? searchedProperty.landarea_admin : "N/A"}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Covered Area:</span>{" "}
                    {hasUsableValue(searchedProperty.coveredarea_admin) ? searchedProperty.coveredarea_admin : "N/A"}
                  </p>
                 <button
  onClick={() => {
    trackAction('property_view_details', searchedProperty.pin, null, {}, 'supervisor');
    navigate(`/supervisor/property?pin=${encodeURIComponent(searchedProperty.pin)}`);
  }}
  className="mt-3 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
>
  View Full Details
</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Cards */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
        >
          {cardData.map((card, index) => (
            <StatCard 
              key={index} 
              {...card} 
              onClick={() => {
                trackAction(card.action, null, null, {}, 'supervisor');
                if (card.title === 'Total Circles') {
                  setShowCirclesList(!showCirclesList);
                } else {
                  navigate(card.route);
                }
              }} 
            />
          ))}
        </motion.div>

        {/* All Circles List */}
        {showCirclesList && (
          <div className="mb-8 bg-white rounded-xl border border-teal-100 shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">All Circles</h2>
              <button
                onClick={() => setShowCirclesList(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {allCircles.map((circle, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => {
                    trackAction('property_circle_filter', null, null, { circle }, 'supervisor');
                    navigate(`/supervisor/property?circle=${encodeURIComponent(circle)}`);
                  }}
                  className="p-4 bg-gradient-to-r from-teal-50 to-teal-100 border border-teal-200 rounded-xl text-left hover:from-teal-100 hover:to-teal-200 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <FaBuilding className="text-teal-600 text-xl" />
                    <span className="font-semibold text-gray-800 text-lg">{circle}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Assigned by Admin - Circle Cards */}
        {Object.keys(assignedByCircle).length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-3">
              <FaClipboardList className="text-teal-600" />
              Properties Assigned by Circle
            </h2>
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              initial="hidden"
              animate="visible"
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
            >
              {Object.entries(assignedByCircle).map(([circle, count], index) => (
                <StatCard
                  key={circle}
                  title={circle}
                  value={count}
                  icon={<FaBuilding />}
                  onClick={() => {
                    trackAction('property_circle_filter', null, null, { circle }, 'supervisor');
                    navigate(`/supervisor/property?assigned=true&circle=${encodeURIComponent(circle)}`);
                  }}
                />
              ))}
            </motion.div>
          </div>
        )}

        {/* Pie Chart Section */}
        <motion.div
          className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <FaChartPie className="text-teal-600 text-xl mr-2" />
              <h2 className="text-lg font-medium text-gray-700">Property Distribution Overview</h2>
            </div>
            <span className="text-sm text-gray-500">
              Total Properties:{' '}
              <span className="font-semibold text-teal-600">{stats.totalProperties}</span>
            </span>
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Pie Chart */}
            <div className="w-full lg:w-1/2 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value} Properties`, 'Count']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span className="text-sm text-gray-700">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Stats Summary */}
            <div className="w-full lg:w-1/2 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-100 rounded-xl">
                  <p className="text-2xl font-bold text-gray-600">{stats.totalProperties}</p>
                  <p className="text-xs text-gray-600 mt-1">Total Properties</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-2xl font-bold text-green-600">{resolvedCount}</p>
                  <p className="text-xs text-gray-600 mt-1">Resolved Properties</p>
                  <p className="text-sm font-semibold text-green-600 mt-1">{resolvedPercentage}%</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <p className="text-2xl font-bold text-red-600">{awaitingCount}</p>
                  <p className="text-xs text-gray-600 mt-1">Flagged/Assigned</p>
                  <p className="text-sm font-semibold text-red-600 mt-1">{flaggedPercentage}%</p>
                </div>
                <div className="text-center p-4 bg-gray-100 rounded-xl">
                  <p className="text-2xl font-bold text-gray-600">{remaining}</p>
                  <p className="text-xs text-gray-600 mt-1">Pending Properties</p>
                  <p className="text-sm font-semibold text-gray-600 mt-1">{remainingPercentage}%</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <div className="text-center">
                  <p className="text-3xl font-bold text-teal-600">
                    {Math.round((stats.resolved / totalProperties) * 100)}%
                  </p>
                  <p className="text-sm text-gray-500 mt-1">Overall Completion Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Legend with colors */}
          <div className="flex flex-wrap justify-center gap-6 mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">
                Resolved: <span className="font-semibold">{resolvedCount}</span>
              </span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">
                Flagged/Assigned: <span className="font-semibold">{awaitingCount}</span>
              </span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-400 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">
                Pending: <span className="font-semibold">{remaining}</span>
              </span>
            </div>
          </div>
        </motion.div>

      </main>
    </div>
  );
};

const StatCard = ({ title, value, icon, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -3, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="group relative bg-white border border-gray-200 rounded-2xl p-6 flex items-center justify-between shadow-sm hover:shadow-lg hover:border-teal-500 transition-all duration-300 focus:outline-none"
    >
      <div>
        <h2 className="text-sm font-medium text-gray-500">{title}</h2>
        <p className="text-3xl font-semibold text-gray-800 mt-1">{value}</p>
      </div>
      <div className="text-teal-600 text-4xl bg-teal-50 p-3 rounded-full group-hover:bg-teal-100 transition-all duration-300">
        {icon}
      </div>
    </motion.button>
  );
};

export default Dashboard;
